import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

/**
 * Collection links (noo.ma) — 1:1 fork of collection-links.js
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 *
 * @extends {Component<Refs>}
 */
class DevCollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  /** @type {boolean} */
  #imagesWarmed = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('pointerenter', this.#warmImages);

    this.#scroll = new Scroller(this.refs.container, { onScroll: this.#handleScroll });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll.destroy();
  }

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    if (event) this.#revealImage(targetIndex, event);
  }

  /**
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, links.length - 1);

    for (const [index, link] of links.entries()) {
      link.setAttribute('aria-current', Boolean(index === selectedIndex).toString());
    }
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    this.#scroll.to(selectedLink);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  #handleScroll = () => {
    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  clearSelections = () => {
    const { links } = this;
    const { images } = this.refs;

    for (const link of links) {
      link.setAttribute('aria-current', 'false');
    }

    if (images) {
      for (const image of images) {
        image.removeAttribute('data-follow');
        image.removeAttribute('reveal');
      }
    }
  };

  /**
   * Warm hover images into the HTTP cache once the section is hovered (fine pointer only).
   * Same idea as sibling color swatches — text-layout images are `loading="lazy"` and hidden until reveal.
   */
  #warmImages = () => {
    if (this.#imagesWarmed) return;

    // Touch / coarse pointers never show the cursor preview — skip and stop listening.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.#imagesWarmed = true;
      this.removeEventListener('pointerenter', this.#warmImages);
      return;
    }

    const { images } = this.refs;
    if (!images?.length) return;

    this.#imagesWarmed = true;
    this.removeEventListener('pointerenter', this.#warmImages);

    for (const image of images) {
      for (const img of image.querySelectorAll('img')) {
        if (!(img instanceof HTMLImageElement)) continue;

        const url = img.currentSrc || img.src;
        if (!url) continue;

        img.loading = 'eager';

        const warm = new Image();
        warm.decoding = 'async';
        warm.src = url;
      }
    }
  };

  /**
   * @param {number} index
   * @param {Event} event
   */
  #revealImage(index, event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images, container } = this.refs;
    const selectedImage = images?.[index];

    if (!selectedImage) return;

    this.#warmImages();

    /** Place sync before `reveal` — rAF would paint one frame at 0,0. */
    const placeAt = (pointerEvent) => {
      // Row band from the cloud mid-line; Y anchors to the hovered link (not past the whole cloud).
      const avoidRect = container.getBoundingClientRect();
      const linkRect = target.getBoundingClientRect();
      const { x, y } = this.#cursorImagePosition(
        pointerEvent.clientX,
        pointerEvent.clientY,
        selectedImage.offsetWidth,
        selectedImage.offsetHeight,
        avoidRect,
        linkRect
      );

      selectedImage.style.setProperty('--x', `${x}px`);
      selectedImage.style.setProperty('--y', `${y}px`);
    };

    for (const image of images) {
      image.removeAttribute('data-follow');
      if (image !== selectedImage) image.removeAttribute('reveal');
    }

    placeAt(event);
    selectedImage.setAttribute('reveal', '');
    placeAt(event);

    /** @type {number | null} */
    let rafId = null;
    /** @type {number | null} */
    let followRafId = null;

    // Enable follow lag on the next frame so the entrance is not animated from top-left.
    followRafId = requestAnimationFrame(() => {
      followRafId = null;
      selectedImage.setAttribute('data-follow', '');
    });

    /** @param {PointerEvent} pointerEvent */
    const updateImagePosition = (pointerEvent) => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        placeAt(pointerEvent);
      });
    };

    const reset = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (followRafId !== null) {
        cancelAnimationFrame(followRafId);
        followRafId = null;
      }
      selectedImage.removeAttribute('data-follow');
      selectedImage.removeAttribute('reveal');
      target.removeEventListener('mousemove', updateImagePosition);
    };

    target.addEventListener('mousemove', updateImagePosition);
    target.addEventListener('mouseleave', reset, { once: true });
  }

  /**
   * Float the preview near the cursor: top-row links → just below that link; bottom-row → just above.
   * Stays clear of the hovered name; clamps to the viewport.
   *
   * @param {number} clientX
   * @param {number} clientY
   * @param {number} imageWidth
   * @param {number} imageHeight
   * @param {DOMRect} avoidRect - Entire `.collection-links__container` box (row mid + side fallback)
   * @param {DOMRect} linkRect - Hovered collection link box
   */
  #cursorImagePosition(clientX, clientY, imageWidth, imageHeight, avoidRect, linkRect) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16;
    const gap = 14;

    const spaceBelowLink = viewportHeight - linkRect.bottom - margin;
    const spaceAboveLink = linkRect.top - margin;
    const spaceRight = viewportWidth - linkRect.right - margin;
    const spaceLeft = linkRect.left - margin;

    const canBelow = spaceBelowLink >= imageHeight + gap;
    const canAbove = spaceAboveLink >= imageHeight + gap;

    // Top row of the cloud → preview below the link; bottom row → above (closer to the cursor than parking past the whole cloud).
    const linkMidY = linkRect.top + linkRect.height / 2;
    const cloudMidY = avoidRect.top + avoidRect.height / 2;
    const linkOnTopRow = linkMidY <= cloudMidY;

    /** @type {boolean} */
    let preferBelow;
    if (linkOnTopRow && canBelow) {
      preferBelow = true;
    } else if (!linkOnTopRow && canAbove) {
      preferBelow = false;
    } else if (canBelow) {
      preferBelow = true;
    } else {
      preferBelow = false;
    }

    /** @type {number} */
    let x = clientX - imageWidth * 0.35;
    /** @type {number} */
    let y;

    if (preferBelow && canBelow) {
      y = linkRect.bottom + gap;
    } else if (!preferBelow && canAbove) {
      y = linkRect.top - imageHeight - gap;
    } else if (canBelow) {
      y = linkRect.bottom + gap;
    } else if (canAbove) {
      y = linkRect.top - imageHeight - gap;
    } else if (spaceRight >= imageWidth + gap || spaceRight >= spaceLeft) {
      x = linkRect.right + gap;
      y = clamp(clientY - imageHeight * 0.35, margin, viewportHeight - imageHeight - margin);
    } else {
      x = linkRect.left - imageWidth - gap;
      y = clamp(clientY - imageHeight * 0.35, margin, viewportHeight - imageHeight - margin);
    }

    x = clamp(x, margin, Math.max(margin, viewportWidth - imageWidth - margin));
    y = clamp(y, margin, Math.max(margin, viewportHeight - imageHeight - margin));

    return { x, y };
  }
}

if (!customElements.get('dev-collection-links-component')) {
  customElements.define('dev-collection-links-component', DevCollectionLinks);
}
